
import React from 'react';
import ProductBasicInfo from './ProductBasicInfo';
import ProductOverview from './ProductOverview';
import ProductDescription from './ProductDescription';
import ProductMedia from './ProductMedia';
import ProductPricing from './ProductPricing';
import ProductInventory from './ProductInventory';
import ProductBenefits from './ProductBenefits';
import ProductFaqs from './ProductFaqs';
import ShopBadge from './ShopBadge';
import FormSelector from './FormSelector';
import { Benefit, Faq } from '@/lib/supabase/products';

interface ProductFormContentProps {
  shopName?: string;
  shopId?: string;
  organizationId?: string | null;
  name: string;
  setName: (value: string) => void;
  overview: string;
  setOverview: (value: string) => void;
  showSummary: boolean;
  setShowSummary: (value: boolean) => void;
  description: string;
  setDescription: (value: string) => void;
  benefits: Benefit[];
  setBenefits: (value: Benefit[]) => void;
  faqs: Faq[];
  setFaqs: (value: Faq[]) => void;
  mediaUrls: string[];
  previewMediaUrls: string[];
  addMediaUrl: (url: string) => void;
  removeMediaUrl: (index: number) => void;
  removeExistingMediaUrl: (index: number) => void;
  productId: string;
  price: string;
  setPrice: (value: string) => void;
  discountPrice: string;
  setDiscountPrice: (value: string) => void;
  currency: string;
  setCurrency: (value: string) => void;
  quantity: number;
  setQuantity: (value: number) => void;
  lowStockThreshold: number;
  setLowStockThreshold: (value: number) => void;
  isOutOfStock: boolean;
  setIsOutOfStock: (value: boolean) => void;
  sku: string;
  setSku: (value: string) => void;
  product?: any;
  formId?: string | null;
  setFormId?: (value: string | null) => void;
  defaultFormId?: string | null;
}

const ProductFormContent = ({
  shopName,
  shopId,
  organizationId,
  name,
  setName,
  overview,
  setOverview,
  showSummary,
  setShowSummary,
  description,
  setDescription,
  benefits,
  setBenefits,
  faqs,
  setFaqs,
  mediaUrls,
  previewMediaUrls,
  addMediaUrl,
  removeMediaUrl,
  removeExistingMediaUrl,
  productId,
  price,
  setPrice,
  discountPrice,
  setDiscountPrice,
  currency,
  setCurrency,
  quantity,
  setQuantity,
  lowStockThreshold,
  setLowStockThreshold,
  isOutOfStock,
  setIsOutOfStock,
  sku,
  setSku,
  product,
  formId,
  setFormId,
  defaultFormId
}: ProductFormContentProps) => {
  return (
    <>
      <ShopBadge shopName={shopName} shopId={shopId} organizationId={organizationId} />
      
      <ProductBasicInfo name={name} setName={setName} />
      
      <ProductOverview 
        overview={overview} 
        setOverview={setOverview} 
        showSummary={showSummary}
        setShowSummary={setShowSummary}
      />
      
      <ProductDescription description={description} setDescription={setDescription} />
      
      <ProductBenefits benefits={benefits} setBenefits={setBenefits} />
      
      <ProductFaqs faqs={faqs} setFaqs={setFaqs} />
      
      <ProductMedia 
        mediaUrls={mediaUrls || []}
        previewMediaUrls={previewMediaUrls}
        addMediaUrl={addMediaUrl}
        removeMediaUrl={removeMediaUrl}
        removeExistingMediaUrl={removeExistingMediaUrl}
        name={name} 
        productId={productId}
        shopId={shopId}
      />
      
      <ProductPricing 
        price={price} 
        setPrice={setPrice} 
        reducedPrice={discountPrice} 
        setReducedPrice={setDiscountPrice}
        currency={currency}
        setCurrency={setCurrency}
      />
      
      <ProductInventory 
        id={product?.id}
        name={name}
        quantity={quantity}
        setQuantity={setQuantity}
        lowStockThreshold={lowStockThreshold}
        setLowStockThreshold={setLowStockThreshold}
        isOutOfStock={isOutOfStock}
        setIsOutOfStock={setIsOutOfStock}
        sku={sku}
        setSku={setSku}
      />
      
      {/* Form selector section - Always show it when shopId is available */}
      {shopId && (
        <div className="bg-white dark:bg-[#1a1d2d] p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Formulaire de commande</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Associez un formulaire à ce produit pour collecter des informations lors de la commande.
          </p>
          <FormSelector 
            shopId={shopId}
            selectedFormId={formId || null}
            onSelectForm={setFormId || (() => {})}
            defaultFormId={defaultFormId}
          />
        </div>
      )}
    </>
  );
};

export default ProductFormContent;
